import { t } from "@lingui/macro";
import * as Square from "@square/web-sdk";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@whoosh/design-system/src/components/controls";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { useGetSQUIPForWeb } from "@whoosh/features/src/Square/features/SquareCardEntry/hooks/useGetSQUIPForWeb";

import { SquareGiftCardEntryModalProps } from "./types";

export const SquareGiftCardEntryModal = ({
  isOpen,
  onCardEntryCancel,
  onCardEntryComplete,
  loading,
  setLoading,
}: SquareGiftCardEntryModalProps) => {
  const squareApplicationId = useGetSQUIPForWeb();

  const [manualGiftCard, setManualGiftCard] = useState<
    Square.GiftCard | undefined
  >(undefined);

  const [isValid, setIsValid] = useState(false);

  const cleanUpForm = useCallback(() => {
    setLoading(false);
    if (manualGiftCard) {
      manualGiftCard?.detach();
      setManualGiftCard(undefined);
    }
    setIsValid(false);
  }, [manualGiftCard, setLoading, setIsValid, setManualGiftCard]);

  const handleClose = () => {
    onCardEntryCancel();
    cleanUpForm();
  };

  useEffect(() => {
    if (!isOpen) return;
    const attachGiftCardContainer = async () => {
      const p = await Square.payments(squareApplicationId);
      const squareGiftCard = await p?.giftCard({});
      await squareGiftCard?.attach("#gift-card-container");
      squareGiftCard?.addEventListener(
        "focusClassAdded",
        handleGiftCardValidityChange
      );
      squareGiftCard?.addEventListener(
        "focusClassRemoved",
        handleGiftCardValidityChange
      );
      setManualGiftCard(squareGiftCard);
    };

    attachGiftCardContainer();
  }, [squareApplicationId, isOpen]);

  const handleGiftCardValidityChange = (
    e: Square.SqEvent<Square.GiftCardInputEvent>
  ) => {
    let value = e.detail.currentState.isCompletelyValid;
    setIsValid(value);
  };

  const handleAddGiftCard = useCallback(async () => {
    const token = await manualGiftCard?.tokenize();
    const squareNonce = token?.token;
    if (squareNonce) {
      onCardEntryComplete({
        nonce: squareNonce,
        card: {
          brand: "",
          expirationMonth: 0,
          expirationYear: 0,
          lastFourDigits: "",
          postalCode: "",
          prepaidType: "",
          type: "",
        },
      });
      cleanUpForm();
    }
  }, [manualGiftCard, onCardEntryComplete, cleanUpForm]);

  const isLoading = loading;

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={handleClose}
      upperTitle={t`Add Gift Card`}
      disableContentScroll
      content={
        <Box flex={1} px={6} height="100%" pb={6}>
          <Box flex={1}>
            <form id="payment-form">
              <div id="gift-card-container" />
            </form>
          </Box>

          <Box borderTopWidth={1} borderColor="outline-default" pt={5}>
            <Button
              onPress={() => handleAddGiftCard()}
              accessibilityLabel={t`Add Gift Card`}
              isDisabled={isLoading || !isValid}
              isLoading={isLoading}
              label={t`Add Gift Card`}
            />
          </Box>
        </Box>
      }
    />
  );
};
